import type { HoleRange, SimplifiedPayment } from '@/lib/db/types';
import { holeRangeIndices } from '@/lib/scoring/hole-range';

export interface PayoutDisplayRow {
  fromKey: string;
  toKey: string;
  fromName: string;
  toName: string;
  amountCents: number;
  involvesViewer: boolean;
}

export function formatPayoutDollars(cents: number): string {
  return `$${Math.abs(cents / 100).toFixed(0)}`;
}

export function firstName(displayName: string): string {
  const part = displayName.trim().split(/\s+/)[0];
  return part || displayName || 'Player';
}

/** Build who-pays-whom rows from minimized settlement payments. */
export function buildPayoutRows(
  payments: SimplifiedPayment[] | null | undefined,
  players: Array<{ player_key: string; display_name: string; member_id?: string | null }>,
  viewerMemberId?: string | null,
): PayoutDisplayRow[] {
  if (!payments?.length) return [];
  const nameByKey = new Map(players.map((p) => [p.player_key, firstName(p.display_name)]));
  const viewerKey = viewerMemberId
    ? players.find((p) => p.member_id === viewerMemberId)?.player_key
    : undefined;

  return payments
    .filter((p) => p.amount_cents > 0)
    .map((p) => ({
      fromKey: p.from_player_key,
      toKey: p.to_player_key,
      fromName: nameByKey.get(p.from_player_key) || p.from_player_key,
      toName: nameByKey.get(p.to_player_key) || p.to_player_key,
      amountCents: p.amount_cents,
      involvesViewer: Boolean(
        viewerKey && (p.from_player_key === viewerKey || p.to_player_key === viewerKey),
      ),
    }));
}

export function payoutSentence(row: PayoutDisplayRow): string {
  return `${row.fromName} pays ${row.toName} ${formatPayoutDollars(row.amountCents)}`;
}

/** True when at least one player has a recorded hole score. */
export function hasHoleByHoleScores(
  scores: Array<{ strokes: number | null }> | null | undefined,
): boolean {
  return Boolean(scores?.some((s) => s.strokes != null && Number.isFinite(s.strokes)));
}

/**
 * Build 18-slot score arrays keyed by hole_num − 1 for GroupScorecardGrid.
 * Works for front-9, back-9, and full rounds.
 */
export function buildGridScoresByPlayer(
  players: Array<{ player_key: string; member_id?: string | null; display_name: string }>,
  scores: Array<{ player_key: string; hole_num: number; strokes: number | null }>,
  viewerMemberId?: string | null,
): Array<{
  id: string;
  name: string;
  guest: boolean;
  you: boolean;
  scores: (number | null)[];
}> {
  return players.map((p) => {
    const arr = Array(18).fill(null) as (number | null)[];
    scores
      .filter((s) => s.player_key === p.player_key)
      .forEach((s) => {
        if (s.hole_num >= 1 && s.hole_num <= 18 && s.strokes != null) {
          arr[s.hole_num - 1] = s.strokes;
        }
      });
    return {
      id: p.member_id || p.player_key,
      name: p.display_name,
      guest: !p.member_id,
      you: Boolean(viewerMemberId && p.member_id === viewerMemberId),
      scores: arr,
    };
  });
}

export function scorecardRangeForHoles(
  holesPlayed: HoleRange | null | undefined,
  courseHoles?: Array<{ hole_num: number }>,
) {
  const base = holeRangeIndices(holesPlayed || 'full');
  if (!courseHoles?.length) return base;

  const holeNums = courseHoles.map((h) => h.hole_num).sort((a, b) => a - b);
  const maxNum = holeNums[holeNums.length - 1] ?? 18;
  // Nine-hole courses / combos: never invent a back nine the card doesn't have.
  if (maxNum <= 9) {
    const count = Math.min(9, holeNums.length);
    return {
      start: 0,
      end: count,
      indices: Array.from({ length: count }, (_, i) => i),
    };
  }

  const available = new Set(holeNums.map((n) => n - 1));
  const indices = base.indices.filter((i) => available.has(i));
  if (!indices.length) return base;
  return { start: indices[0], end: indices[indices.length - 1] + 1, indices };
}

/** Gross totals per player when hole-by-hole data is missing. */
export function totalsOnlyRows(
  players: Array<{ player_key: string; display_name: string; member_id?: string | null }>,
  scores: Array<{ player_key: string; strokes: number | null }>,
  viewerMemberId?: string | null,
): Array<{ id: string; name: string; you: boolean; total: number | null }> {
  return players.map((p) => {
    const holeScores = scores
      .filter((s) => s.player_key === p.player_key && s.strokes != null)
      .map((s) => s.strokes as number);
    const total = holeScores.length ? holeScores.reduce((a, b) => a + b, 0) : null;
    return {
      id: p.member_id || p.player_key,
      name: p.display_name,
      you: Boolean(viewerMemberId && p.member_id === viewerMemberId),
      total,
    };
  });
}
